// Palette presets for the Look portal — a curated, PORTAL-ONLY library of BARE palettes (colours +
// mapping mode + b/c/s) the Owner builds a Look FROM. Per ADR-0044 these are INDEPENDENT of the camera's
// default Looks (/palettes.txt) — not a mirror — so they never reach a camera. Owners add their own on
// top, persisted to localStorage.

export interface PalettePreset {
  name: string;
  colors: string[]; // "#rrggbb"
  mapMode: "nearest" | "luminance" | "gradient";
  brightness: number;
  contrast: number;
  saturation: number;
}

// The curated preset library (revamp, ADR-0044): bold + distinct, loosely grouped by family. Grammar:
// name|#hex,#hex,…|b=,c=,s=,map=nearest|luminance|gradient. Bare only — a Dither style + effects make a Look.
const PRESET_LINES = `
thermal|#000000,#2C0A6B,#5E1A8A,#9E1A6B,#D72D4F,#F0641E,#F9A800,#FFE000,#FFFFF0|b=0,c=0,s=0,map=luminance
rainbow|#001A66,#0080FF,#00E0D0,#40D000,#E0E000,#FF8000,#FF2000,#FFFFF0|b=0,c=0,s=0,map=gradient
CMYK|#0093D3,#CC016B,#FFF10D,#000000,#FFFFFF|b=0,c=0,s=0
t-lollipop|#151640,#3F6D9E,#F783B0,#E6F2EF|b=0,c=0,s=0,map=luminance
apollo|#090A14,#10141F,#241527,#151D28,#1E1D39,#172038,#341C27,#411D31,#202E37,#19332D,#402751,#4D2B32,#253A5E,#602C2C,#752438,#394A50,#25562E,#7A367B,#7A4841,#A53030,#884B2B,#3C5E8B,#A23E8C,#577277,#468232,#C65197,#CF573C,#AD7757,#BE772B,#4F8FBA,#819796,#75A743,#DA863E,#C09473,#DF84A5,#DE9E41,#A8B5B2,#73BED3,#D7B594,#A8CA58,#E8C170,#C7CFCC,#A4DDDB,#D0DA91,#E7D5B3,#EBEDE9|b=0,c=0,s=0
Grayscale|#000000,#111111,#222222,#333333,#444444,#555555,#666666,#777777,#888888,#999999,#AAAAAA,#BBBBBB,#CCCCCC,#DDDDDD,#EEEEEE,#FFFFFF|b=0,c=0,s=0
spotlight|#08080C,#26262E,#4C4C58,#8A8A96,#C8C8D2,#3FE0FF,#FF5FD2|b=0,c=0,s=0
blue-glitter|#000000,#050A1A,#0E1E5A,#1E46C8,#2A7AE0,#3FD0FF,#B0E8FF|b=-15,c=25,s=0
acid-tape|#0A0A0A,#FFE500|b=0,c=0,s=0,map=luminance
fluoro-zine|#0E1030,#FF2E88,#FFC2DE|b=0,c=0,s=0,map=luminance
grape-soda|#240A3C,#6E2AA0,#B7F542,#EFFFCB|b=0,c=0,s=0,map=luminance
tokyo-night|#15161E,#1A1B26,#F7768E,#FF9E64,#E0AF68,#9ECE6A,#73DACA,#7AA2F7,#BB9AF7,#C0CAF5|b=0,c=0,s=0
synthwave-sunset|#1A0B2E,#3B0F6F,#7B1FA2,#C2185B,#FF2E88,#FF5E5B,#FF9E45,#FFD166,#FFF3B0|b=0,c=0,s=0,map=luminance
acid-rave|#020A02,#0B3D0B,#00A82D,#39FF14,#9DFF00,#E8FF66|b=0,c=0,s=0,map=luminance
laser-grid|#02030A,#050B2E,#1B6FFF,#18C7FF,#0AF0FF,#FF6A00,#FFB020|b=0,c=0,s=0
blacklight-uv|#0A0014,#2D0A4E,#6A00F4,#B100E8,#F20089,#00F5D4,#B5FF3D|b=0,c=0,s=0
vapor-miami|#1B1035,#2E1A6B,#00E5D0,#FF5FA2,#FF9CE8,#C77DFF,#FFD9A0|b=0,c=0,s=0
kodachrome-64|#1A0E08,#3B1518,#6E1F1C,#A82F22,#C85A2A,#E08A2E,#E8B24E,#F0D98A,#F7EFD6|b=0,c=0,s=0,map=luminance
faded-polaroid|#2B2E33,#4A4E4C,#6E6B62,#927F6E,#B29A82,#CBB79C,#DFD2B8,#EDE7D2|b=0,c=-10,s=0,map=luminance
technicolor|#0B0B14,#C41E3A,#E4572E,#F2B400,#1F9E4B,#0C6FB5,#7A2A82,#F5EFE0|b=0,c=0,s=0
Bauhaus Primary|#F4ECE0,#161616,#D42A1C,#F2B705,#1544B0|b=0,c=0,s=0
Swiss Signal|#F7F7F2,#111111,#E30613|b=0,c=0,s=0
Warhol Pop|#FDF2E0,#161016,#FF2D95,#12C2B0,#FFC31F,#6A2C9C|b=0,c=0,s=0
infrared|#0A0000,#2B0000,#5C0000,#8E0000,#C00000,#E82500,#FF6A00,#FFB300,#FFF0C0|b=0,c=0,s=0,map=luminance
sodium-vapour|#0A0500,#241200,#4A2600,#7A4200,#B06600,#E08A10,#FFB53A,#FFE7A0|b=0,c=0,s=0,map=luminance
aurora|#040414,#0B2140,#0E4E55,#147F4E,#33B45C,#74E866,#B8F58A,#E7A8D8,#FBE4F2|b=0,c=0,s=0,map=luminance
oil-slick|#1A0F2E,#2B1B6B,#1F3FA8,#1E86C9,#22C6C0,#2FD98A,#8FE84A,#F5D33A,#F58A2E,#E8459A,#A64BD6,#F2E9F5|b=0,c=0,s=0,map=gradient
bioluminescent|#01040A,#041830,#063A5E,#0A6E7A,#10A88C,#35D9A0,#7FF0C8,#C8FFE8|b=0,c=0,s=0,map=luminance
coral-reef|#05334D,#0A6E8C,#12B5C4,#5FEBD0,#E8452B,#FF7F6E,#FFB25E,#FFE59E|b=0,c=0,s=0
`;

/** Parse one Pixie palette line into a preset, or null if malformed. */
function parsePalettePresetLine(line: string): PalettePreset | null {
  const parts = line.split("|");
  if (parts.length < 2) return null;
  const name = parts[0]!.trim();
  const colors = parts[1]!
    .split(",")
    .map((c) => c.trim())
    .filter(Boolean)
    .map((c) => (c.startsWith("#") ? c : "#" + c).toLowerCase());
  if (!name || colors.length === 0) return null;
  const opts: Record<string, string> = {};
  for (const kv of (parts[2] ?? "").split(",")) {
    const [k, v] = kv.split("=");
    if (k) opts[k.trim().toLowerCase()] = (v ?? "").trim();
  }
  return {
    name,
    colors,
    mapMode:
      opts.map === "gradient" ? "gradient"
        : opts.map === "luminance" || (opts.lum ?? "").toUpperCase() === "ON" ? "luminance"
          : "nearest",
    brightness: Number(opts.b) || 0,
    contrast: Number(opts.c) || 0,
    saturation: Number(opts.s) || 0,
  };
}


export const BUILTIN_PRESETS: PalettePreset[] = PRESET_LINES.trim().split("\n")
  .map(parsePalettePresetLine)
  .filter((p): p is PalettePreset => p !== null);

// ---- user presets (localStorage) — the "add your own" half ----
const USER_KEY = "pixie.lookportal.presets";

/** A stored entry is only a preset if it has the right shape — a corrupt entry is dropped, not
 *  cast (a malformed colors[] would otherwise crash the swatch row / dropdown). */
function isPalettePreset(v: unknown): v is PalettePreset {
  if (typeof v !== "object" || v === null) return false;
  const p = v as Record<string, unknown>;
  return typeof p.name === "string"
    && Array.isArray(p.colors) && p.colors.length > 0 && p.colors.every((c) => typeof c === "string")
    && (p.mapMode === "nearest" || p.mapMode === "luminance" || p.mapMode === "gradient")
    && typeof p.brightness === "number" && typeof p.contrast === "number" && typeof p.saturation === "number";
}

export function loadUserPresets(): PalettePreset[] {
  try {
    const raw = JSON.parse(localStorage.getItem(USER_KEY) ?? "[]");
    return Array.isArray(raw) ? (raw as unknown[]).filter(isPalettePreset) : [];
  } catch {
    return [];
  }
}

/** Save (or replace by name) a user preset; returns the full updated user list. */
export function saveUserPreset(preset: PalettePreset): PalettePreset[] {
  const all = [...loadUserPresets().filter((p) => p.name !== preset.name), preset];
  try {
    localStorage.setItem(USER_KEY, JSON.stringify(all));
  } catch {
    /* storage may be unavailable (private mode) — keep it in-memory for the session */
  }
  return all;
}

/** Remove a user preset by name; returns the updated user list. */
export function deleteUserPreset(name: string): PalettePreset[] {
  const all = loadUserPresets().filter((p) => p.name !== name);
  try {
    localStorage.setItem(USER_KEY, JSON.stringify(all));
  } catch {
    /* ignore */
  }
  return all;
}
